<template>
  <el-dialog
    title="语音问诊"
    :visible.sync="show"
    width="640px"
    :close-on-click-modal="false"
    :modal="false"
    custom-class="voice-consultation-modal"
    @closed="handleClosed"
  >
    <div v-loading="isUploading" class="voice-modal-body">
      <!-- 状态栏 -->
      <div class="voice-status">
        <el-tag :type="statusTagType" size="small" effect="light">
          <i :class="statusIcon" />
          {{ statusText }}
        </el-tag>
        <span v-if="isRecording" class="record-timer">{{ recordTimer }}</span>
      </div>

      <!-- 录音控制 -->
      <div class="voice-control">
        <div class="record-btn-wrapper" @click="toggleRecording">
          <div class="record-btn" :class="{ recording: isRecording }">
            <i :class="isRecording ? 'el-icon-video-pause' : 'el-icon-microphone'" />
          </div>
        </div>
        <p class="record-tip">{{ isRecording ? '正在聆听，点击停止' : '点击麦克风开始语音问诊' }}</p>
      </div>

      <!-- 文本区域 -->
      <div class="voice-text">
        <div class="section-label">识别内容</div>
        <el-input
          v-model="finalResult"
          type="textarea"
          :rows="4"
          placeholder="语音内容将实时显示在这里，您也可以直接编辑修改"
          resize="none"
        />
        <div v-if="interimResult" class="interim-hint">
          <i class="el-icon-loading" /> {{ interimResult }}
        </div>
      </div>

      <!-- 诊断结果预览 -->
      <div v-if="diagnosisResult" class="voice-result">
        <div class="result-header">
          <i class="el-icon-first-aid-kit" />
          <span>智能诊断结果</span>
        </div>
        <div class="result-body">
          <div class="result-row">
            <label>中医病：</label>
            <span>{{ diagnosisResult.chDiagName || '-' }}</span>
          </div>
          <div class="result-row">
            <label>主诉词：</label>
            <span>{{ diagnosisResult.symptomText || '-' }}</span>
          </div>
          <div v-if="diagnosisResult.matchedSymList && diagnosisResult.matchedSymList.length" class="result-row">
            <label>匹配症状：</label>
            <div class="sym-tags">
              <el-tag
                v-for="(sym, idx) in diagnosisResult.matchedSymList"
                :key="idx"
                size="mini"
                type="primary"
                effect="plain"
              >
                {{ sym.name }}
              </el-tag>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleCancel">取消</el-button>
      <el-button
        size="small"
        type="primary"
        :loading="isUploading"
        :disabled="!canSubmit"
        @click="handleConfirm"
      >
        {{ diagnosisResult ? '确认并填充' : '提交识别' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { postFileData } from '@/api/ik/data/dataApi'

export default {
  name: 'VoiceConsultationModal',
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      show: false,
      isRecording: false,
      speechStatus: '未连接',
      interimResult: '',
      finalResult: '',
      fullText: '',
      isUploading: false,
      diagnosisResult: null,

      // 录音
      mediaRecorder: null,
      audioChunks: [],
      audioBlob: null,
      audioFile: null,
      audioStream: null,
      recordStartTime: null,
      recordDuration: 0,
      recordTimerInterval: null,

      // WebSocket 实时识别
      websocket: null,
      audioContext: null,
      scriptProcessor: null,
      audioInput: null,
      speechTaskId: '',
      hasStartedTranscription: false,

      // Token
      currentToken: '',
      speechAppKey: 'bYgC41rWqjKoRXb3',
      speechWebSocketUrl: 'wss://nls-gateway.cn-shanghai.aliyuncs.com/ws/v1',
      accessKeyId: process.env.VUE_APP_ALIYUN_ACCESS_KEY_ID,
      accessKeySecret: process.env.VUE_APP_ALIYUN_ACCESS_KEY_SECRET,
      tokenRefreshTimer: null
    }
  },
  computed: {
    statusText() {
      if (this.isUploading) return '正在提交...'
      if (this.isRecording) return '录音中 · 实时转写'
      if (this.speechStatus === '已连接') return '已连接 · 等待录音'
      if (this.speechStatus === '连接中...') return '正在连接服务...'
      return '点击麦克风开始语音问诊'
    },
    statusTagType() {
      if (this.isUploading) return 'warning'
      if (this.isRecording) return 'danger'
      if (this.speechStatus === '已连接') return 'success'
      return 'info'
    },
    statusIcon() {
      if (this.isUploading) return 'el-icon-loading'
      if (this.isRecording) return 'el-icon-microphone'
      if (this.speechStatus === '已连接') return 'el-icon-check'
      return 'el-icon-info'
    },
    recordTimer() {
      const m = Math.floor(this.recordDuration / 60).toString().padStart(2, '0')
      const s = (this.recordDuration % 60).toString().padStart(2, '0')
      return `${m}:${s}`
    },
    canSubmit() {
      return (this.finalResult || '').trim().length > 0 || this.audioFile !== null
    }
  },
  watch: {
    visible(val) {
      this.show = val
      if (val) {
        this.initToken()
        this.resetData()
      }
    },
    show(val) {
      this.$emit('update:visible', val)
    }
  },
  beforeDestroy() {
    this.stopRecording()
    this.disconnectWebSocket()
    if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer)
    if (this.recordTimerInterval) clearInterval(this.recordTimerInterval)
  },
  methods: {
    // ========== Token 管理 ==========
    async initToken() {
      if (this.currentToken) return
      await this.fetchToken()
    },
    async fetchToken() {
      if (!this.accessKeyId || !this.accessKeySecret) {
        this.$message.error('缺少 AccessKey 配置，无法获取 Token')
        return
      }
      try {
        const params = {
          AccessKeyId: this.accessKeyId,
          Action: 'CreateToken',
          Version: '2019-02-28',
          Format: 'JSON',
          RegionId: 'cn-shanghai',
          SignatureMethod: 'HMAC-SHA1',
          SignatureVersion: '1.0',
          SignatureNonce: this.generateUUID(),
          Timestamp: this.getISO8601Time()
        }
        const canonicalizedQuery = this.canonicalizedQuery(params)
        const stringToSign = `GET&${this.percentEncode('/')}&${this.percentEncode(canonicalizedQuery)}`
        const signature = await this.sign(stringToSign, this.accessKeySecret + '&')
        const queryString = `Signature=${signature}&${canonicalizedQuery}`
        const url = `https://nls-meta.cn-shanghai.aliyuncs.com/?${queryString}`

        const response = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }})
        const data = await response.json()
        if (data.Token) {
          this.currentToken = data.Token.Id || data.Token
          const expireTime = data.Token.ExpireTime || data.ExpireTime
          const refreshDelay = Math.max((expireTime - Date.now() / 1000 - 300) * 1000, 60000)
          if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer)
          this.tokenRefreshTimer = setTimeout(() => this.fetchToken(), refreshDelay)
        }
      } catch (error) {
        console.error('获取Token失败:', error)
      }
    },
    percentEncode(str) {
      if (!str) return ''
      return encodeURIComponent(str).replace(/\+/g, '%20').replace(/\*/g, '%2A').replace(/%7E/g, '~')
    },
    canonicalizedQuery(params) {
      return Object.keys(params).sort().map(key => {
        return `${this.percentEncode(key)}=${this.percentEncode(params[key])}`
      }).join('&')
    },
    async sign(stringToSign, secret) {
      const encoder = new TextEncoder()
      const keyData = encoder.encode(secret)
      const messageData = encoder.encode(stringToSign)
      const cryptoKey = await crypto.subtle.importKey('raw', keyData, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign'])
      const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData)
      const base64 = btoa(String.fromCharCode(...new Uint8Array(signature)))
      return this.percentEncode(base64)
    },
    getISO8601Time() {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      return `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}T${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}Z`
    },
    generateUUID() {
      const hex = '0123456789abcdef'
      let uuid = ''
      for (let i = 0; i < 32; i++) uuid += hex[Math.floor(Math.random() * 16)]
      return uuid
    },

    // ========== 录音与实时识别 ==========
    async toggleRecording() {
      if (this.isRecording) {
        this.stopRecording()
      } else {
        if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
          try {
            await this.connectWebSocket()
          } catch (error) {
            this.$message.error('语音识别服务连接失败')
            return
          }
        }
        this.startRecording()
      }
    },
    connectWebSocket() {
      return new Promise((resolve, reject) => {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          this.speechStatus = '已连接'
          resolve()
          return
        }
        const token = this.currentToken
        if (!token) { reject(new Error('Token为空')); return }
        const socketUrl = `${this.speechWebSocketUrl}?token=${token}&appkey=${this.speechAppKey}`
        try {
          this.websocket = new WebSocket(socketUrl)
          this.speechStatus = '连接中...'
          const timeout = setTimeout(() => {
            if (this.websocket && this.websocket.readyState !== WebSocket.OPEN) {
              this.websocket.close()
              this.websocket = null
              this.speechStatus = '连接超时'
              reject(new Error('连接超时'))
            }
          }, 5000)
          this.websocket.onopen = () => { clearTimeout(timeout); this.speechStatus = '已连接'; resolve() }
          this.websocket.onerror = (error) => { clearTimeout(timeout); this.speechStatus = '连接错误'; reject(error) }
          this.websocket.onclose = () => {
            this.speechStatus = '未连接'
            this.isRecording = false
            this.hasStartedTranscription = false
            this.websocket = null
          }
          this.websocket.onmessage = (event) => {
            try {
              const message = JSON.parse(event.data)
              const headerName = message.header?.name
              if (headerName === 'TranscriptionStarted') {
                console.log('语音识别任务已启动')
              } else if (headerName === 'TranscriptionResultChanged') {
                this.interimResult = message.payload.result
              } else if (headerName === 'SentenceEnd') {
                const result = message.payload.result
                if (result) {
                  this.fullText += (this.fullText ? ' ' : '') + result
                  this.finalResult = this.fullText
                  this.interimResult = ''
                }
              } else if (headerName === 'TranscriptionCompleted') {
                this.hasStartedTranscription = false
              }
            } catch (e) {
              console.error('解析消息失败:', e)
            }
          }
        } catch (error) {
          this.speechStatus = '未连接'
          reject(error)
        }
      })
    },
    async startRecording() {
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) return
      if (this.isRecording) return
      try {
        if (!this.hasStartedTranscription) {
          this.sendStartTranscription()
          await new Promise(resolve => setTimeout(resolve, 300))
        }
        this.audioStream = await navigator.mediaDevices.getUserMedia({
          audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true }
        })
        this.audioChunks = []
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4')
        this.mediaRecorder = new MediaRecorder(this.audioStream, { mimeType })
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) this.audioChunks.push(event.data)
        }
        this.mediaRecorder.onstop = () => {
          this.audioBlob = new Blob(this.audioChunks, { type: mimeType })
          const ext = mimeType.includes('webm') ? 'webm' : (mimeType.includes('mp4') ? 'mp4' : 'ogg')
          const filename = `voice_${this.formatDate(new Date())}.${ext}`
          this.audioFile = new File([this.audioBlob], filename, { type: mimeType })
        }
        this.mediaRecorder.start(100)

        this.audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 })
        this.audioInput = this.audioContext.createMediaStreamSource(this.audioStream)
        this.scriptProcessor = this.audioContext.createScriptProcessor(2048, 1, 1)
        this.scriptProcessor.onaudioprocess = (event) => {
          if (!this.isRecording || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) return
          const inputData = event.inputBuffer.getChannelData(0)
          const inputData16 = new Int16Array(inputData.length)
          for (let i = 0; i < inputData.length; ++i) {
            inputData16[i] = Math.max(-1, Math.min(1, inputData[i])) * 0x7FFF
          }
          this.websocket.send(inputData16.buffer)
        }
        this.audioInput.connect(this.scriptProcessor)
        this.scriptProcessor.connect(this.audioContext.destination)

        this.isRecording = true
        this.recordStartTime = Date.now()
        this.recordDuration = 0
        this.recordTimerInterval = setInterval(() => {
          this.recordDuration = Math.floor((Date.now() - this.recordStartTime) / 1000)
        }, 1000)
        this.speechStatus = '录音中...'
      } catch (error) {
        console.error('录音失败:', error)
        this.$message.error('无法启动录音: ' + error.message)
        this.isRecording = false
      }
    },
    formatDate(date) {
      const pad = n => String(n).padStart(2, '0')
      return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
    },
    sendStartTranscription() {
      this.speechTaskId = this.generateUUID()
      const msg = {
        header: {
          appkey: this.speechAppKey,
          namespace: 'SpeechTranscriber',
          name: 'StartTranscription',
          task_id: this.speechTaskId,
          message_id: this.generateUUID()
        },
        payload: {
          format: 'pcm',
          sample_rate: 16000,
          enable_intermediate_result: true,
          enable_punctuation_prediction: true,
          enable_inverse_text_normalization: true
        }
      }
      this.websocket.send(JSON.stringify(msg))
      this.hasStartedTranscription = true
    },
    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop()
      }
      if (this.scriptProcessor) { this.scriptProcessor.disconnect(); this.scriptProcessor = null }
      if (this.audioInput) { this.audioInput.disconnect(); this.audioInput = null }
      if (this.audioStream) { this.audioStream.getTracks().forEach(track => track.stop()); this.audioStream = null }
      if (this.audioContext) { this.audioContext.close(); this.audioContext = null }
      this.isRecording = false
      this.interimResult = ''
      if (this.recordTimerInterval) { clearInterval(this.recordTimerInterval); this.recordTimerInterval = null }
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN && this.hasStartedTranscription) {
        const stopMessage = {
          header: {
            message_id: this.generateUUID(),
            task_id: this.speechTaskId,
            namespace: 'SpeechTranscriber',
            name: 'StopTranscription',
            appkey: this.speechAppKey
          },
          payload: {}
        }
        try { this.websocket.send(JSON.stringify(stopMessage)) } catch (e) { console.error(e) }
        this.speechStatus = '已连接'
      }
    },
    disconnectWebSocket() {
      if (this.websocket) { this.websocket.close(); this.websocket = null }
      this.speechStatus = '未连接'
      this.isRecording = false
      this.hasStartedTranscription = false
    },

    // ========== 提交与回填 ==========
    async handleConfirm() {
      // 如果已经获取到诊断结果，直接填充并关闭
      if (this.diagnosisResult) {
        this.$emit('complete', this.diagnosisResult)
        this.$message.success('语音问诊结果已填充至诊断表单')
        this.show = false
        this.resetData()
        return
      }

      // 首次提交：先停止录音并上传
      if (this.isRecording) this.stopRecording()
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        await new Promise(resolve => {
          const check = setInterval(() => {
            if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
              clearInterval(check); resolve()
            }
          }, 50)
        })
      }
      await new Promise(resolve => setTimeout(resolve, 200))

      const textResult = this.finalResult || this.fullText || ''
      if (!textResult.trim() && !this.audioFile) {
        this.$message.warning('没有识别到语音内容，也未录制到音频')
        return
      }

      this.isUploading = true
      try {
        const formData = new FormData()
        if (this.audioFile) {
          formData.append('file', this.audioFile)
        } else {
          const emptyBlob = new Blob([], { type: 'audio/webm' })
          formData.append('file', new File([emptyBlob], 'empty.webm', { type: 'audio/webm' }))
        }

        const dataObj = { data: { chiefComplaint: textResult, storeCode: this.$store.state.user.storeCode }}
        const dataBlob = new Blob([JSON.stringify(dataObj)], { type: 'application/json; charset=utf-8' })
        formData.append('data', dataBlob)

        const res = await postFileData(
          'clinic/treatment/treatment/diagrecord/do/checkchiefComplaint',
          formData
        )

        const responseBody = (res && res.data !== undefined) ? res.data : res
        const responseData = (responseBody && responseBody.data !== undefined) ? responseBody.data : (responseBody || {})
        const matchResult = String(responseData?.matchResult || '')

        if (matchResult === '2') {
          this.$message.warning('症状描述过于简单，无法判断。请详细描述具体不适。')
          this.isUploading = false
          return
        }
        if (matchResult === '3') {
          this.$message.warning('请描述您的病情症状，以便提供准确诊断建议。')
          this.isUploading = false
          return
        }

        // 提取 matchedSymList 并拼接主诉词
        const matchedSymList = responseData?.matchedSymList || []
        const symptomText = matchedSymList.map(item => item.name).join(' ')

        // 必须有诊断编码才能继续
        if (!responseData?.chDiagDictionary?.code) {
          this.$message.warning('未获取到诊断编码，请检查接口返回')
          this.isUploading = false
          return
        }

        // 保存结果，按钮自动变为"确认并填充"
        this.diagnosisResult = {
          text: textResult,
          responseData: responseBody,
          chDiagDictionaryCode: responseData?.chDiagDictionary?.code,
          chDiagName: responseData?.chDiagName,
          chiefComplaint: responseData?.chiefComplaint,
          symptomText: symptomText, // 空格拼接的主诉词
          matchedSymList: matchedSymList, // 原始症状数组
          matchResult: responseData?.matchResult,
          asrText: responseData?.asrText,
          analysis: responseData?.analysis
        }
      } catch (error) {
        console.error('提交失败:', error)
        this.$message.error('提交失败: ' + (error.message || '未知错误'))
      } finally {
        this.isUploading = false
      }
    },

    handleCancel() {
      this.stopRecording()
      this.resetData()
      this.show = false
    },

    handleClosed() {
      this.stopRecording()
      this.disconnectWebSocket()
      this.resetData()
    },

    resetData() {
      this.finalResult = ''
      this.interimResult = ''
      this.fullText = ''
      this.audioBlob = null
      this.audioFile = null
      this.audioChunks = []
      this.diagnosisResult = null
      this.recordDuration = 0
      if (this.recordTimerInterval) { clearInterval(this.recordTimerInterval); this.recordTimerInterval = null }
    }
  }
}
</script>

<style scoped lang="scss">
.voice-modal-body {
  padding: 4px;
}

.voice-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  .record-timer {
    font-size: 14px;
    color: #f56c6c;
    font-weight: 600;
    font-family: 'Roboto Mono', monospace;
  }
}

.voice-control {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;

  .record-btn-wrapper {
    cursor: pointer;
    margin-bottom: 12px;
  }

  .record-btn {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, #165dff 0%, #4a9eff 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(22, 93, 255, 0.3);
    transition: all 0.3s ease;

    i {
      font-size: 28px;
      color: #fff;
    }

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 24px rgba(22, 93, 255, 0.4);
    }

    &:active {
      transform: scale(0.95);
    }

    &.recording {
      background: linear-gradient(135deg, #c0392b 0%, #e74c3c 100%);
      box-shadow: 0 4px 16px rgba(231, 76, 60, 0.3);
      animation: pulse-record 1.5s ease-in-out infinite;
    }
  }

  .record-tip {
    font-size: 13px;
    color: #606266;
    margin: 0;
  }
}

.voice-text {
  margin-bottom: 16px;

  .section-label {
    font-size: 14px;
    font-weight: 600;
    color: #1d2129;
    margin-bottom: 8px;
    display: flex;
    align-items: center;

    &::before {
      content: '';
      display: inline-block;
      width: 4px;
      height: 14px;
      background: #165dff;
      border-radius: 2px;
      margin-right: 8px;
    }
  }

  .interim-hint {
    margin-top: 8px;
    font-size: 13px;
    color: #909399;

    i {
      margin-right: 4px;
    }
  }
}

.voice-result {
  margin-top: 16px;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 16px;
  background: #f7f8fa;

  .result-header {
    font-size: 14px;
    font-weight: 600;
    color: #1d2129;
    margin-bottom: 12px;
    display: flex;
    align-items: center;

    i {
      color: #165dff;
      margin-right: 6px;
      font-size: 16px;
    }
  }

  .result-body {
    .result-row {
      display: flex;
      margin-bottom: 8px;
      font-size: 13px;

      &:last-child {
        margin-bottom: 0;
      }

      label {
        color: #606266;
        width: 60px;
        flex-shrink: 0;
      }

      span {
        color: #1d2129;
        font-weight: 500;
      }

      .sym-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }
    }
  }
}

@keyframes pulse-record {
  0% {
    box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.4);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(231, 76, 60, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(231, 76, 60, 0);
  }
}
</style>
